import type { Canvas } from 'fabric';
import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { VariantKey } from '../store/atoms/slideAtom';

export interface UseCanvasHistoryParams {
  readonly desktopCanvasRef: RefObject<Canvas | null>;
  readonly mobileCanvasRef: RefObject<Canvas | null>;
}

/**
 * Gerencia o histórico de undo/redo do canvas por chave de variante.
 * Mantém snapshots serializados do estado do canvas (máx. 40 por variante).
 *
 * @param params - Refs do canvas desktop e mobile
 * @returns Funções para empurrar snapshots, desfazer/refazer e estado dos botões
 */
export function useCanvasHistory({ desktopCanvasRef, mobileCanvasRef }: UseCanvasHistoryParams) {
  const historyRef = useRef<Record<string, string[]>>({});
  const historyPointerRef = useRef<Record<string, number>>({});

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateHistoryState = useCallback((key: string) => {
    const pointer = historyPointerRef.current[key] ?? -1;
    const length = (historyRef.current[key] ?? []).length;
    setCanUndo(pointer > 0);
    setCanRedo(pointer < length - 1);
  }, []);

  const pushSnapshot = useCallback(
    (key: string) => {
      const device = key.endsWith('desktop') ? 'desktop' : 'mobile';
      const canvas = device === 'desktop' ? desktopCanvasRef.current : mobileCanvasRef.current;
      if (!canvas) return;
      if (!historyRef.current[key]) historyRef.current[key] = [];
      if (historyPointerRef.current[key] === undefined) historyPointerRef.current[key] = -1;
      const snapshot = JSON.stringify(canvas.toObject(['data']));
      const pointer = historyPointerRef.current[key];
      historyRef.current[key] = historyRef.current[key].slice(0, pointer + 1);
      historyRef.current[key].push(snapshot);
      if (historyRef.current[key].length > 40) historyRef.current[key].shift();
      historyPointerRef.current[key] = historyRef.current[key].length - 1;
      updateHistoryState(key);
    },
    [desktopCanvasRef, mobileCanvasRef, updateHistoryState],
  );

  const resetHistory = useCallback((key: string) => {
    historyRef.current[key] = [];
    historyPointerRef.current[key] = -1;
  }, []);

  const undo = useCallback(
    (variantKey: VariantKey, saveCurrentSlide: (k: VariantKey) => void) => {
      const device = variantKey.endsWith('desktop') ? 'desktop' : 'mobile';
      const canvas = device === 'desktop' ? desktopCanvasRef.current : mobileCanvasRef.current;
      if (!canvas) return;
      const pointer = historyPointerRef.current[variantKey] ?? 0;
      if (pointer <= 0) return;
      historyPointerRef.current[variantKey] = pointer - 1;
      const snapshot = (historyRef.current[variantKey] ?? [])[pointer - 1] ?? '';
      if (!snapshot) return;
      const parsed = JSON.parse(snapshot) as Record<string, unknown>;
      void canvas.loadFromJSON(parsed).then(() => {
        canvas.requestRenderAll();
        saveCurrentSlide(variantKey);
        updateHistoryState(variantKey);
      });
    },
    [desktopCanvasRef, mobileCanvasRef, updateHistoryState],
  );

  const redo = useCallback(
    (variantKey: VariantKey, saveCurrentSlide: (k: VariantKey) => void) => {
      const device = variantKey.endsWith('desktop') ? 'desktop' : 'mobile';
      const canvas = device === 'desktop' ? desktopCanvasRef.current : mobileCanvasRef.current;
      if (!canvas) return;
      const pointer = historyPointerRef.current[variantKey] ?? 0;
      const length = (historyRef.current[variantKey] ?? []).length;
      if (pointer >= length - 1) return;
      historyPointerRef.current[variantKey] = pointer + 1;
      const snapshot = (historyRef.current[variantKey] ?? [])[pointer + 1] ?? '';
      if (!snapshot) return;
      const parsed = JSON.parse(snapshot) as Record<string, unknown>;
      void canvas.loadFromJSON(parsed).then(() => {
        canvas.requestRenderAll();
        saveCurrentSlide(variantKey);
        updateHistoryState(variantKey);
      });
    },
    [desktopCanvasRef, mobileCanvasRef, updateHistoryState],
  );

  return {
    pushSnapshot,
    resetHistory,
    updateHistoryState,
    undo,
    redo,
    canUndo,
    canRedo,
  } as const;
}
